// supervisor-loop-outcome-journal.ts
//
// Split out of supervisor-loop.ts (was 253 lines, over the 200-line budget —
// review 260909-p6-m2 INFO). Owns the LAST step of one control-loop tick:
// turning a routed outcome into the terminal `ticket_state_changed` +
// `attempt_finished` journal events (or "back to todo" for a retryable
// outcome) and committing them. Pure translation + one `store.commit` — no
// dispatch/routing logic of its own, so it is safe to split away from
// runOneSupervisorTick's dispatch half. persistPostRouteAttemptState moved to
// supervisor-loop-post-route-attempt-state.ts (review 03a L6: 198/200 dòng).
import { randomUUID } from "node:crypto";
import type { WorkerDecisionsJournalFields } from "./attempt-finished-worker-decisions.js";
import type { MissionCommitInput, MissionStore } from "./mission-store.js";
import { needsDecisionAttemptReason } from "./needs-decision-attempt-reason.js";
import { formatAttemptEndLine } from "./run-progress-lines.js";
import { extractStderrTailExcerptForProgressLine } from "./session-failure-reasons.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";
import type { ReportedGaps } from "./ticket-reported-gaps.js";

export interface JournalAttemptOutcomeInput {
  store: MissionStore;
  ticketId: string;
  attemptNo: number;
  route: RouteOutcomeResult;
  /** Already formatted by supervisor-forensics-archival.ts's formatSchemaErrorDetail — undefined when there is none to record. */
  schemaErrorDetail?: string;
  /**
   * Instrumentation 2026-09-15: the worker's raw `result` text tail for a
   * `no_result_json_found`/`invalid_structured_output_schema` session —
   * ALREADY redacted + capped by supervisor-forensics-archival.ts's
   * formatRawResultTailForJournal. Undefined for every other outcome, and
   * ignored below for any route that carries no failure verdict (`done`,
   * `quota_sleep`), so it can only ever ride on a retry_decision record.
   */
  rawResultTail?: string;
  /**
   * A2: the worker's own coverage limit (`uncovered_areas` / `remaining_work`
   * off the validated result — ticket-reported-gaps.ts). Journaled on the
   * `done` attempt_finished only: a rejected attempt's self-report is not a
   * completion report. Never synthesized when the worker sent nothing.
   */
  reportedGaps?: ReportedGaps;
  /**
   * V2-1 E13: `worker_decisions` (+ `worker_decisions_truncated`) from the
   * worker's non-empty `decisions[]`, already redacted and capped
   * (attempt-finished-worker-decisions.ts). Journaled on THIS attempt's
   * attempt_finished whatever the route; undefined ⇒ no field (baseline payload).
   */
  workerDecisionFields?: WorkerDecisionsJournalFields;
  /** Phase 03 (R-15, Req 2): operator-facing progress sink — undefined ⇒ silent (unit tests). */
  progress?: (line: string) => void;
  /** Phase 03: clock for the ■ line's elapsed time — undefined ⇒ `() => new Date()`. */
  now?: () => Date;
  /** Phase 03: wall-clock at journalAttemptStart — required to compute mm:ss when `progress` is set. */
  attemptStartedAt?: Date;
  /** 06g R5: this attempt's price from the worker's `usage` (undefined ⇒ ■ prints none). */
  attemptCostUsd?: number;
}

/**
 * Journal the classifier verdict alongside attempt_finished (dogfooding P9b:
 * mission ME-2026-c3985e blocked two tickets with the reason nowhere
 * queryable — journal only had attempt_started/attempt_finished/
 * ticket_state_changed, F03/F10 require the verifier's verdict to be
 * recorded). `route.decision.evidence` is ALREADY redacted by
 * recordAttemptOutcome (retry-policy.ts's redactEvidence) — only present for
 * retry_decision routes (a real failure/escalation); "done"/"quota_sleep"
 * carry no failure verdict to record.
 */
/** Returns the journal seq of the `attempt_finished` just committed (last event of the batch). */
export async function journalAttemptOutcome(input: JournalAttemptOutcomeInput): Promise<number> {
  const { route, ticketId, attemptNo } = input;

  const terminalStatus =
    route.kind === "done"
      ? "done"
      : route.kind === "retry_decision" && route.decision.outcome === "blocked"
        ? "blocked"
        : route.kind === "retry_decision" && route.decision.outcome === "escalate_waiting_human"
          ? "blocked"
          : undefined;

  const verdictFields =
    route.kind === "retry_decision"
      ? {
          category: route.decision.category,
          reasons: route.decision.evidence !== undefined ? [route.decision.evidence] : [],
          ...(input.schemaErrorDetail !== undefined
            ? { schema_error_detail: input.schemaErrorDetail }
            : {}),
          ...(input.rawResultTail !== undefined ? { raw_result_tail: input.rawResultTail } : {}),
        }
      : // B4 (code-reviewer 260916 M2): needs_decision KHÔNG phải một verdict lỗi,
        // nhưng bỏ trống hoàn toàn thì `attempt_finished` không nói được vì sao
        // attempt dừng — người đọc journal thấy một attempt kết thúc, ticket về
        // "todo", và không có dấu vết nào của câu hỏi đang chờ.
        // `category` + `reasons` phải đi CÙNG NHAU: mission-state-reducer.ts:186
        // chỉ ghi `ticket_last_verdict` khi có đủ cả hai, thiếu một là rơi hết.
        route.kind === "needs_decision"
        ? {
            category: "needs_decision",
            // 06j M3: wording shared with the F-50 status row (it parses it back).
            reasons: [needsDecisionAttemptReason(route.questionId, route.autoApplied === true)],
          }
        : {};

  const gapFields =
    route.kind === "done" && input.reportedGaps !== undefined
      ? {
          ...(input.reportedGaps.uncovered_areas !== undefined
            ? { uncovered_areas: input.reportedGaps.uncovered_areas }
            : {}),
          ...(input.reportedGaps.remaining_work !== undefined
            ? { remaining_work: input.reportedGaps.remaining_work }
            : {}),
        }
      : {};

  const events: MissionCommitInput[] = [
    {
      event_id: randomUUID(),
      delta: {
        kind: "attempt_finished",
        ticket: ticketId,
        attemptNo,
        ...verdictFields,
        // Orchestrator recovery: WHY the ticket stopped, only on a stop; an
        // untagged stop is `other` (fail closed — never recovery-eligible).
        ...(terminalStatus === "blocked" && route.kind === "retry_decision"
          ? { blocked_by: route.decision.blocked_by ?? "other" }
          : {}),
        ...gapFields,
        ...input.workerDecisionFields,
      },
    },
  ];
  if (terminalStatus !== undefined) {
    events.unshift({
      event_id: randomUUID(),
      delta: { kind: "ticket_state_changed", ticket: ticketId, status: terminalStatus },
    });
  } else {
    // Retryable (including quota_sleep) => ticket goes back to "todo" so the
    // scheduler can pick it up again on a later tick. Quota sleep itself is
    // tracked separately in quota-sleep.json (wake_at) — the CALLER must
    // consult `isAwake()` before its next tick, since the scheduler alone
    // only sees "todo" (ready) vs a dependency graph, it has no notion of
    // "ready but asleep".
    events.unshift({
      event_id: randomUUID(),
      delta: { kind: "ticket_state_changed", ticket: ticketId, status: "todo" },
    });
  }
  // `ticket_state_changed` is unshifted ⇒ the batch's last seq is attempt_finished's (N4).
  const attemptFinishedSeq = await input.store.commit(events);

  // Req 2 (R-15): ■ printed AFTER attempt_finished is committed — same rule
  // as ▶ in supervisor-loop-attempt-start.ts (journal write is the source of
  // truth). This is the ONE call site shared by both the feature-ticket
  // dispatch path and the review-candidate path (its tick result is
  // otherwise swallowed by dispatchCandidateReviewSessionIfNeeded), so a
  // review attempt gets its ■ line too.
  if (input.progress && input.attemptStartedAt !== undefined) {
    const now = input.now ?? (() => new Date());
    const elapsedMs = now().getTime() - input.attemptStartedAt.getTime();
    // Phase 04 (F1b): ≤120 chars after `stderr_tail:` in the SAME reasons[0]
    // just committed above — undefined route (done/quota_sleep/needs_decision)
    // or a reason without a stderr tail both fall through to no excerpt.
    const reasons = "reasons" in verdictFields ? verdictFields.reasons : undefined;
    const stderrExcerpt = extractStderrTailExcerptForProgressLine(reasons);
    input.progress(
      formatAttemptEndLine(ticketId, route, elapsedMs, stderrExcerpt, input.attemptCostUsd),
    );
  }
  return attemptFinishedSeq;
}
